import React, { useEffect, lazy, Suspense } from 'react';
import { Route, Routes, Navigate, useLocation, useNavigationType } from 'react-router-dom';
import { Analytics } from '@vercel/analytics/react';
import * as Sentry from '@sentry/react';
import ErrorBoundary from './components/ErrorBoundary';
import {
  SignedIn,
  SignedOut,
  RedirectToSignIn,
  useAuth,
  useUser
} from '@clerk/clerk-react';
import { useSubscription } from './hooks/useSubscription';
import LandingPage from './components/LandingPage';
import Privacy from './components/Privacy';
import Terms from './components/Terms';
import Compatibility from './components/Compatibility';
import WebsiteFeedbackTool from './components/seo/WebsiteFeedbackTool';
import WebsiteAnnotationTool from './components/seo/WebsiteAnnotationTool';
import ForWordPress from './components/seo/ForWordPress';
import ForShopify from './components/seo/for/ForShopify';
import ForWebflow from './components/seo/for/ForWebflow';
import ForSquarespace from './components/seo/for/ForSquarespace';
import ForWix from './components/seo/for/ForWix';
import ForNextjs from './components/seo/for/ForNextjs';
import HowItWorks from './components/seo/HowItWorks';
import VsMarkerIo from './components/seo/VsMarkerIo';
import VsBugherd from './components/seo/VsBugherd';
import VsUserback from './components/seo/VsUserback';
import VsUsersnap from './components/seo/VsUsersnap';
import VsPastel from './components/seo/VsPastel';
import VsRuttl from './components/seo/VsRuttl';
import VsMarkupIo from './components/seo/VsMarkupIo';
import VsFeedbucket from './components/seo/VsFeedbucket';
import Loader from './components/Loader';

// App and auth routes are code-split so the pre-rendered marketing pages ship a much
// smaller main bundle. Public marketing routes above stay eager: they are already in
// the HTML and must swap in without a loading state.
const lazyRoute = (loader) => lazy(loader);
const Dashboard = lazyRoute(() => import('./components/DashboardNew'));
const NotesBoard = lazyRoute(() => import('./components/NotesBoard'));
const WidgetAuth = lazyRoute(() => import('./components/WidgetAuth'));
const Billing = lazyRoute(() => import('./components/Billing'));
const SettingsPage = lazyRoute(() => import('./components/Settings'));
const Team = lazyRoute(() => import('./components/Team'));
const Login = lazyRoute(() => import('./components/Login'));
const SignUpPage = lazyRoute(() => import('./components/SignUp'));
const AcceptInvite = lazyRoute(() => import('./components/AcceptInvite'));
const ActivityFeed = lazyRoute(() => import('./components/ActivityFeed'));
const Integrations = lazyRoute(() => import('./components/Integrations'));
const SlackCallback = lazyRoute(() => import('./components/SlackCallback'));
const JiraCallback = lazyRoute(() => import('./components/JiraCallback'));
const LinearCallback = lazyRoute(() => import('./components/LinearCallback'));
const AsanaCallback = lazyRoute(() => import('./components/AsanaCallback'));
const GuestView = lazyRoute(() => import('./components/GuestView'));
const Trash = lazyRoute(() => import('./components/Trash'));
const SSOCallback = lazyRoute(() => import('./components/SSOCallback'));

// Loading gate only for authenticated routes — public routes render immediately
function AuthGate({ children }) {
  const { isLoaded } = useAuth();
  if (!isLoaded) {
    return <Loader fullscreen />;
  }
  return children;
}

function ProtectedRoute({ children }) {
  return (
    <AuthGate>
      <SignedIn>{children}</SignedIn>
      <SignedOut><RedirectToSignIn /></SignedOut>
    </AuthGate>
  );
}

// Gate for Pro/Team features — free users get sent to billing
function ProGate({ children }) {
  const { isFree, loading } = useSubscription();
  if (loading) {
    return <Loader fullscreen />;
  }
  if (isFree) return <Navigate to="/billing" replace />;
  return children;
}

// Client-side navigation keeps the previous scroll position, so a footer link opened a new
// page scrolled to the bottom. Reset to the top on push/replace; leave back/forward (POP)
// and in-page hash links alone so the browser can restore where the user was.
function ScrollToTop() {
  const { pathname, hash } = useLocation();
  const navType = useNavigationType();
  useEffect(() => {
    if (navType === 'POP' || hash) return;
    window.scrollTo(0, 0);
  }, [pathname, hash, navType]);
  return null;
}

function SentryUserSync() {
  const { user, isLoaded } = useUser();
  useEffect(() => {
    if (isLoaded && user) {
      Sentry.setUser({ id: user.id, email: user.primaryEmailAddress?.emailAddress });
    } else if (isLoaded) {
      Sentry.setUser(null);
    }
  }, [isLoaded, user]);
  return null;
}

function HomePage() {
  const { isLoaded, isSignedIn } = useAuth();
  // Show landing immediately if Clerk hasn't loaded yet
  if (!isLoaded) return <LandingPage />;
  return <LandingPage isSignedIn={isSignedIn} />;
}

function App() {
  // Pre-rendered pages set this flag so the landing page skips its entrance fade on first
  // paint (no blink when React swaps in). Clear it after mount so later SPA visits animate.
  useEffect(() => { if (typeof window !== 'undefined') window.__caydaPrerendered = false; }, []);

  return (
    <ErrorBoundary>
    <ScrollToTop />
    <SentryUserSync />
    <>
    <div className="min-h-screen">
      <Suspense fallback={<Loader fullscreen />}>
      <Routes>
        {/* Public routes — render without waiting for Clerk */}
        <Route path="/" element={<HomePage />} />
        <Route path="/sign-in/*" element={<AuthGate><Login /></AuthGate>} />
        <Route path="/sign-up/*" element={<AuthGate><SignUpPage /></AuthGate>} />
        <Route path="/widget-auth" element={<WidgetAuth />} />
        <Route path="/sso-callback" element={<SSOCallback />} />
        <Route path="/invite/:token" element={<AuthGate><AcceptInvite /></AuthGate>} />
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/terms" element={<Terms />} />
        <Route path="/compatibility" element={<Compatibility />} />
        <Route path="/website-feedback-tool" element={<WebsiteFeedbackTool />} />
        <Route path="/website-annotation-tool" element={<WebsiteAnnotationTool />} />
        <Route path="/for/wordpress" element={<ForWordPress />} />
        <Route path="/for/shopify" element={<ForShopify />} />
        <Route path="/for/webflow" element={<ForWebflow />} />
        <Route path="/for/squarespace" element={<ForSquarespace />} />
        <Route path="/for/wix" element={<ForWix />} />
        <Route path="/for/nextjs" element={<ForNextjs />} />
        <Route path="/how-it-works" element={<HowItWorks />} />
        <Route path="/vs/marker-io" element={<VsMarkerIo />} />
        <Route path="/vs/bugherd" element={<VsBugherd />} />
        <Route path="/vs/userback" element={<VsUserback />} />
        <Route path="/vs/usersnap" element={<VsUsersnap />} />
        <Route path="/vs/pastel" element={<VsPastel />} />
        <Route path="/vs/ruttl" element={<VsRuttl />} />
        <Route path="/vs/markup-io" element={<VsMarkupIo />} />
        <Route path="/vs/feedbucket" element={<VsFeedbucket />} />
        <Route path="/g/:token" element={<GuestView />} />

        {/* Protected routes — wait for Clerk, then gate */}
        <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
        <Route path="/activity" element={<ProtectedRoute><ProGate><ActivityFeed /></ProGate></ProtectedRoute>} />
        <Route path="/notes" element={<ProtectedRoute><NotesBoard /></ProtectedRoute>} />
        <Route path="/trash" element={<ProtectedRoute><Trash /></ProtectedRoute>} />
        <Route path="/settings" element={<ProtectedRoute><SettingsPage /></ProtectedRoute>} />
        <Route path="/integrations" element={<ProtectedRoute><Integrations /></ProtectedRoute>} />
        <Route path="/integrations/slack/callback" element={<ProtectedRoute><SlackCallback /></ProtectedRoute>} />
        <Route path="/integrations/jira/callback" element={<ProtectedRoute><JiraCallback /></ProtectedRoute>} />
        <Route path="/integrations/linear/callback" element={<ProtectedRoute><LinearCallback /></ProtectedRoute>} />
        <Route path="/integrations/asana/callback" element={<ProtectedRoute><AsanaCallback /></ProtectedRoute>} />
        <Route path="/team" element={<ProtectedRoute><Team /></ProtectedRoute>} />
        <Route path="/billing" element={<ProtectedRoute><Billing /></ProtectedRoute>} />

        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      </Suspense>
    </div>
    <Analytics />
    </>
    </ErrorBoundary>
  );
}

export default App;
